import { buildCustomCsv, CSVDataType, DateTimeFormat, getLocalDateTime } from 'src/common';
import { InitiateReportRequest } from 'src/features/clients/client/reportsV2/store/types';

function getToggle(value: boolean): string {
  return value ? 'Yes' : 'No';
}

export function buildReportParametersCsv(params: InitiateReportRequest): string {
  const csvRows: CSVDataType[] = [];
  csvRows.push({
    'Includes Logo': getToggle(params.includeLogo),
    'Includes Cover Letter': getToggle(params.includeCoverLetter),
    'Includes Appendix': getToggle(params.includeAppendix),
  });
  csvRows.push({});

  if (params.contributionsAndWithdrawals) {
    csvRows.push({
      'Report Name': 'Contributions & Withdrawals',
      'From Date': getLocalDateTime(params.contributionsAndWithdrawals.fromDate, DateTimeFormat.DateTimeShort),
      'To Date': getLocalDateTime(params.contributionsAndWithdrawals.toDate, DateTimeFormat.DateTimeShort),
    });
    csvRows.push({});
  }
  if (params.assetAllocation) {
    csvRows.push({
      'Report Name': 'Asset Allocation',
      'As At Date': getLocalDateTime(params.assetAllocation.asAtDate, DateTimeFormat.DateTimeShort),
      'Include Asset Category': getToggle(params.assetAllocation.includeAssetCategory),
      'Include Detaible Table': getToggle(params.assetAllocation.includeDetailTable),
      'Apply Exclusion List': getToggle(params.assetAllocation.applyExclusionList),
    });
    csvRows.push({});
  }
  if (params.assetClassPerformance) {
    csvRows.push({
      'Report Name': 'Asset Class Performance',
      'From Date': getLocalDateTime(params.assetClassPerformance.fromDate, DateTimeFormat.DateTimeShort),
      'To Date': getLocalDateTime(params.assetClassPerformance.toDate, DateTimeFormat.DateTimeShort),
      'Exclude Franking Credits': getToggle(params.assetClassPerformance.excludeFrankingCredits),
      'Show Annualised Returns': getToggle(params.assetClassPerformance.showAnnualisedReturns),
      'Apply Exclusion List': getToggle(params.assetClassPerformance.applyExclusionList),
      'Exclude Capital Income Returns': getToggle(params.assetClassPerformance.excludeCapitalIncomeReturns),
      'Include Allocation Change': getToggle(params.assetClassPerformance.includeAllocationChange),
      'Include Returns Summary': getToggle(params.assetClassPerformance.includeReturnsSummary),
    });
    csvRows.push({});
  }
  if (params.holdingsVsTargetVariance) {
    csvRows.push({
      'Report Name': 'Holdings Vs Target Variance',
      'As At Date': getLocalDateTime(params.holdingsVsTargetVariance.asAtDate, DateTimeFormat.DateTimeShort),
      'Apply Exclusion List': getToggle(params.holdingsVsTargetVariance.applyExclusionList),
      'Include Summary': getToggle(params.holdingsVsTargetVariance.includeSummary),
    });
    csvRows.push({});
  }
  if (params.cashTransactions) {
    csvRows.push({
      'Report Name': 'Cash Transactions',
      'From Date': getLocalDateTime(params.cashTransactions.fromDate, DateTimeFormat.DateTimeShort),
      'To Date': getLocalDateTime(params.cashTransactions.toDate, DateTimeFormat.DateTimeShort),
      'Sort By': params.cashTransactions.sortBy,
    });
    csvRows.push({});
  }
  if (params.expenses) {
    csvRows.push({
      'Report Name': 'Expenses',
      'From Date': getLocalDateTime(params.expenses.fromDate, DateTimeFormat.DateTimeShort),
      'To Date': getLocalDateTime(params.expenses.toDate, DateTimeFormat.DateTimeShort),
      'Exclude Expense Type': getToggle(params.expenses.excludeExpenseType),
    });
    csvRows.push({});
  }
  if (params.incomeReport) {
    csvRows.push({
      'Report Name': 'Income',
      'From Date': getLocalDateTime(params.incomeReport.fromDate, DateTimeFormat.DateTimeShort),
      'To Date': getLocalDateTime(params.incomeReport.toDate, DateTimeFormat.DateTimeShort),
      'Exclude Cash': getToggle(params.incomeReport.excludeCash),
      'Show Outstanding Payment Only': getToggle(params.incomeReport.showOutstandingPaymentOnly),
      'Sort By': params.incomeReport.sortBy,
      'Group By': params.incomeReport.groupBy,
    });
    csvRows.push({});
  }
  if (params.securityPerformance) {
    csvRows.push({
      'Report Name': 'Security Performance',
      'From Date': getLocalDateTime(params.securityPerformance.fromDate, DateTimeFormat.DateTimeShort),
      'To Date': getLocalDateTime(params.securityPerformance.toDate, DateTimeFormat.DateTimeShort),
      'Exclude Franking Credits': getToggle(params.securityPerformance.excludeFrankingCredits),
      'Show Annualised Returns': getToggle(params.securityPerformance.showAnnualisedReturns),
      'Apply Exclusion List': getToggle(params.securityPerformance.applyExclusionList),
      'Exclude Capital Income Returns': getToggle(params.securityPerformance.excludeCapitalIncomeReturns),
      'Group By': params.securityPerformance.groupBy,
    });
    csvRows.push({});
  }
  if (params.portfolioSnapshot) {
    csvRows.push({
      'Report Name': 'Portfolio Snapshot',
      'From Date': getLocalDateTime(params.portfolioSnapshot.fromDate, DateTimeFormat.DateTimeShort),
      'To Date': getLocalDateTime(params.portfolioSnapshot.toDate, DateTimeFormat.DateTimeShort),
    });
    csvRows.push({});
  }
  if (params.portfolioPerformance) {
    csvRows.push({
      'Report Name': 'Portfolio Performance',
      'From Date': getLocalDateTime(params.portfolioPerformance.fromDate, DateTimeFormat.DateTimeShort),
      'To Date': getLocalDateTime(params.portfolioPerformance.toDate, DateTimeFormat.DateTimeShort),
      'Exclude Franking Credits': getToggle(params.portfolioPerformance.excludeFrankingCredits),
      'Show Annualised Returns': getToggle(params.portfolioPerformance.showAnnualisedReturns),
      'Apply Exclusion List': getToggle(params.portfolioPerformance.applyExclusionList),
      'Include After Fees Return': getToggle(params.portfolioPerformance.includeAfterFeesReturn),
    });
    csvRows.push({});
  }
  if (params.portfolioValuation) {
    csvRows.push({
      'Report Name': 'Portfolio Valuation',
      'As At Date': getLocalDateTime(params.portfolioValuation.asAtDate, DateTimeFormat.DateTimeShort),
      'Exclude Cost Base': getToggle(params.portfolioValuation.excludeCostBase),
      'Exclude Market': getToggle(params.portfolioValuation.excludeMarket),
      'Exclude Security Type': getToggle(params.portfolioValuation.excludeSecurityType),
      'Exclude GICS': getToggle(params.portfolioValuation.excludeGICS),
      'Exclude Gains Losses': getToggle(params.portfolioValuation.excludeGainsLosses),
      'Exclude Avg Unit Cost': getToggle(params.portfolioValuation.excludeAvgUnitCost),
      'Exclude Est Income Yield': getToggle(params.portfolioValuation.excludeEstIncomeYield),
      'Exclude Est Gross Income Yield': getToggle(params.portfolioValuation.excludeEstGrossIncomeYield),
      'Exclude Fx Gains Losses': getToggle(params.portfolioValuation.excludeFXGainsLosses),
      'Exclude Local Price Value': getToggle(params.portfolioValuation.excludeLocalPriceValue),
      'Exclude Total Returns since inception': getToggle(params.portfolioValuation.excludeTotalReturn),
      'Group By': params.portfolioValuation.groupBy,
      'Sort By': params.portfolioValuation.sortBy,
    });
    csvRows.push({});
  }
  if (params.securityContribution) {
    csvRows.push({
      'Report Name': 'Security Contribution',
      'From Date': getLocalDateTime(params.securityContribution.fromDate, DateTimeFormat.DateTimeShort),
      'To Date': getLocalDateTime(params.securityContribution.toDate, DateTimeFormat.DateTimeShort),
      'Exclude Franking Credits': getToggle(params.securityContribution.excludeFrankingCredits),
      'Show Annualised Returns': getToggle(params.securityContribution.showAnnualisedReturns),
      'Apply Exclusion List': getToggle(params.securityContribution.applyExclusionList),
      'Exclude Capital Income Returns': getToggle(params.securityContribution.excludeCapitalIncomeReturns),
      'Include Detail Table': getToggle(params.securityContribution.includeDetailTable),
      'Group By': params.securityContribution.groupBy,
    });
    csvRows.push({});
  }
  if (params.taxSummary) {
    csvRows.push({
      'Report Name': 'Tax Summary',
      'From Date': getLocalDateTime(params.taxSummary.fromDate, DateTimeFormat.DateTimeShort),
      'To Date': getLocalDateTime(params.taxSummary.toDate, DateTimeFormat.DateTimeShort),
    });
    csvRows.push({});
  }
  if (params.taxInvoice) {
    csvRows.push({
      'Report Name': 'Tax Invoice',
      'From Date': getLocalDateTime(params.taxInvoice.fromDate, DateTimeFormat.DateTimeShort),
      'To Date': getLocalDateTime(params.taxInvoice.toDate, DateTimeFormat.DateTimeShort),
    });
    csvRows.push({});
  }
  if (params.investmentIncomeTaxComponent) {
    csvRows.push({
      'Report Name': 'Investment Income Tax',
      'From Date': getLocalDateTime(params.investmentIncomeTaxComponent.fromDate, DateTimeFormat.DateTimeShort),
      'To Date': getLocalDateTime(params.investmentIncomeTaxComponent.toDate, DateTimeFormat.DateTimeShort),
    });
    csvRows.push({});
  }
  if (params.transactionHistory) {
    csvRows.push({
      'Report Name': 'Transaction History',
      'From Date': getLocalDateTime(params.transactionHistory.fromDate, DateTimeFormat.DateTimeShort),
      'To Date': getLocalDateTime(params.transactionHistory.toDate, DateTimeFormat.DateTimeShort),
      'Exclude Cash': getToggle(params.transactionHistory.excludeCash),
      'Include Distribution': getToggle(params.transactionHistory.includeDistribution),
      'Exclude Asset Description': getToggle(params.transactionHistory.excludeAssetDescription),
      'Exclude Accounts': getToggle(params.transactionHistory.excludeAccounts),
      'Include Brokerage': getToggle(params.transactionHistory.includeBrokerage),
      'Include GST': getToggle(params.transactionHistory.includeGST),
      'Include Other Charges': getToggle(params.transactionHistory.includeOtherCharges),
    });
    csvRows.push({});
  }
  if (params.realisedCapitalGainsAndLosses) {
    csvRows.push({
      'Report Name': 'Realised Capital Gains and Losses',
      'From Date': getLocalDateTime(params.realisedCapitalGainsAndLosses.fromDate, DateTimeFormat.DateTimeShort),
      'To Date': getLocalDateTime(params.realisedCapitalGainsAndLosses.toDate, DateTimeFormat.DateTimeShort),
      'Include Summary': getToggle(params.realisedCapitalGainsAndLosses.includeSummary),
    });
    csvRows.push({});
  }
  if (params.unrealisedCapitalGainsAndLosses) {
    csvRows.push({
      'Report Name': 'Unrealised Capital Gains and Losses',
      'As At Date': getLocalDateTime(params.unrealisedCapitalGainsAndLosses.asAtDate, DateTimeFormat.DateTimeShort),
      'Show Cost Base Adjustments': getToggle(params.unrealisedCapitalGainsAndLosses.showCostBaseAdjustments),
    });
    csvRows.push({});
  }
  if (params.unrealisedNonCGT) {
    csvRows.push({
      'Report Name': 'Unrealised Non-CGT Gains and Losses',
      'As At Date': getLocalDateTime(params.unrealisedNonCGT.asAtDate, DateTimeFormat.DateTimeShort),
    });
    csvRows.push({});
  }
  return buildCustomCsv(csvRows);
}
